import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Form, Input, Radio, Button, Alert, Tag, Space, Typography, Modal, Spin, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { authenticatedFetch } from "@/libs/api";
import { TARGET_LABELS, MODE_META, formatTime } from "../../downstreamSync/syncStatus";

const { Text, Paragraph } = Typography;

const MODE_HELP = {
  OFF: "Approvals queue nothing for this downstream.",
  SHADOW: "Approvals record what would be sent. Nothing is sent.",
  ON: "Approvals queue it and the background worker sends it.",
};

/**
 * Configuration → Onboarding Config: what a successful approval sets off outside the platform,
 * and the keys it uses. Superadmin only (the menu hides it, the endpoints refuse anyone else).
 * The secret key is write-only: the backend stores it encrypted and never sends it back.
 */
export default function OnboardingConfig() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authenticatedFetch("/api/downstream-sync/config");
      const json = await res.json();
      if (json.success) setData(json.data);
      else message.error(json.message || "Failed to load the settings");
    } catch (err) {
      message.error("Failed to load the settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PageLayout active="onboardingConfig">
      <div style={{ padding: 24, maxWidth: 880 }}>
        <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }} align="start">
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>Onboarding Config</Typography.Title>
            <Text type="secondary">
              What a successful approval sets off outside the platform. Rows it queues are under
              <Button type="link" size="small" style={{ padding: "0 4px" }} onClick={() => navigate("/group-management/onboard")}>
                System Onboard → Sync
              </Button>
            </Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        </Space>

        {!data ? (
          <Spin spinning={loading}><div style={{ height: 160 }} /></Spin>
        ) : (
          <>
            {!data.installed && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message="The settings table does not exist yet"
                description="Run backend/migrations/downstream_sync_prod.sql. Until then sync is off: approvals work and queue nothing."
              />
            )}
            {data.encryptionKeyProblem && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Secret keys can't be saved or used"
                description={`${data.encryptionKeyProblem}. Set one in the backend's environment (64 hex characters) and restart the backend.`}
              />
            )}
            {data.targets.map((t) => (
              <TargetCard key={t.target} target={t} disabled={!data.installed} onSaved={setData} />
            ))}
          </>
        )}
      </div>
    </PageLayout>
  );
}

/**
 * Required only while the form's mode is ON. Reads the mode when it validates, not when it
 * last rendered, so switching to On and saving straight away is still checked.
 * @param {boolean} [alreadySaved] a stored value counts (the secret key is write-only)
 */
const neededToSend = (alreadySaved = false) => ({ getFieldValue }) => ({
  validator(_, value) {
    if (getFieldValue("mode") !== "ON" || alreadySaved || String(value || "").trim()) return Promise.resolve();
    return Promise.reject(new Error("Needed to send"));
  },
});

function TargetCard({ target, disabled, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const mode = Form.useWatch("mode", form);

  useEffect(() => {
    form.setFieldsValue({
      mode: target.mode,
      endpointUrl: target.endpointUrl,
      accessKey: target.accessKey,
      secretKey: "",
    });
  }, [form, target]);

  const save = async (values) => {
    setSaving(true);
    try {
      const res = await authenticatedFetch(`/api/downstream-sync/config/${target.target}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const json = await res.json();
      if (!json.success) {
        message.error(json.message || "Save failed");
        return;
      }
      message.success(`${TARGET_LABELS[target.target] || target.target}: saved`);
      onSaved(json.data);
    } catch (err) {
      message.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onFinish = (values) => {
    if (values.mode === "ON" && target.mode !== "ON") {
      Modal.confirm({
        title: "Turn sending on?",
        content: "The worker starts sending what is queued (Queued rows), one every second or so. Recorded-only rows are not sent unless you retry them.",
        okText: "Turn on",
        onOk: () => save(values),
      });
      return;
    }
    save(values);
  };

  const meta = MODE_META[target.mode] || {};
  return (
    <Card
      title={TARGET_LABELS[target.target] || target.target}
      extra={
        <Space>
          {target.paused && <Tag color="error">Paused</Tag>}
          <Tag color={meta.color}>{meta.label || target.mode}</Tag>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      {target.paused && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Paused since ${formatTime(target.paused.at)}: ${target.paused.reason}`}
          description="Nothing is sent while paused. Check the access key and secret key, then save: saving resumes sending."
        />
      )}
      <Form form={form} layout="vertical" onFinish={onFinish} disabled={disabled} autoComplete="off">
        <Form.Item name="mode" label="Mode" extra={MODE_HELP[mode]}>
          <Radio.Group optionType="button" buttonStyle="solid">
            <Radio.Button value="OFF">Off</Radio.Button>
            <Radio.Button value="SHADOW">Shadow — records only</Radio.Button>
            <Radio.Button value="ON">On — sends</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="endpointUrl"
          label="URL"
          extra="Base URL of the old Powow portal; the request goes to /api/powowUser/addPowowUser.php under it."
          required={mode === "ON"}
          dependencies={["mode"]}
          rules={[neededToSend()]}
        >
          <Input placeholder="https://…" />
        </Form.Item>
        <Form.Item
          name="accessKey"
          label="Access key"
          required={mode === "ON"}
          dependencies={["mode"]}
          rules={[neededToSend()]}
        >
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="secretKey"
          label="Secret key"
          extra={target.secretKeySet
            ? "Saved (stored encrypted, never shown). Leave blank to keep it; type a new one to replace it."
            : "Not set yet."}
          required={mode === "ON" && !target.secretKeySet}
          dependencies={["mode"]}
          rules={[neededToSend(target.secretKeySet)]}
        >
          <Input.Password
            autoComplete="new-password"
            placeholder={target.secretKeySet ? "•••••••• (saved)" : "Not set"}
            visibilityToggle={false}
          />
        </Form.Item>
        {target.openStates && (
          <Paragraph type="secondary">
            Only these states are sent (DOWNSTREAM_SYNC_{target.target}_STATES): {target.openStates.join(", ")}.
          </Paragraph>
        )}
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Text type="secondary">
            {target.updateTime
              ? `Last saved ${formatTime(target.updateTime)}${target.updateByEmail ? ` by ${target.updateByEmail}` : ""}`
              : "Not saved from this page yet"}
          </Text>
          <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
        </Space>
      </Form>
    </Card>
  );
}
